import React, { Component } from 'react'
import { Redirect } from 'react-router-dom'
import { Segment, Table, Grid, Button, Icon, Loader } from 'semantic-ui-react'
import { PDFExport } from '@progress/kendo-react-pdf'

import { formatName } from '../../utils/nameUtils'
import { skillsForTerm, skillsUsedBy, MACRO_SKILL_LABELS } from './paceGrid'
import './Pace.css'

const SKILL_CLASSES = {
  L: 'pace-skill-listening',
  S: 'pace-skill-speaking',
  R: 'pace-skill-reading',
  W: 'pace-skill-writing',
}

// One page per subject, in the order the official form prints them. GMRC and
// Makabansa share the last page.
const PAGES = [
  { title: 'READING AND LITERACY', subjects: ['Reading and Literacy'] },
  { title: 'LANGUAGE', subjects: ['Language'] },
  { title: 'MATHEMATICS', subjects: ['Mathematics'] },
  { title: 'GOOD MANNERS AND RIGHT CONDUCT (GMRC)', subjects: ['GMRC', 'Makabansa'] },
]

const SUBJECT_HEADINGS = {
  GMRC: 'GOOD MANNERS AND RIGHT CONDUCT (GMRC)',
  Makabansa: 'MAKABANSA',
}

const GENERAL_INSTRUCTIONS =
  'Teachers shall accomplish this form on a regular basis throughout the term, ' +
  'not as a one-time entry, by recording the learner’s level of attainment for ' +
  'each learning competency using the appropriate descriptor: A (Advancing), ' +
  'B (Benchmarking), C (Connecting), D (Developing), or E (Emerging). Entries ' +
  'shall be made in the corresponding term only after the learner has engaged in ' +
  'a series of formative tasks and activities and has completed a relevant ' +
  'summative assessment. The assigned level shall be based on sufficient and ' +
  'varied evidence of learning, including learner outputs, observations, ' +
  'anecdotal records, and other assessment results, and shall reflect the ' +
  'learner’s most consistent level of performance.'

/**
 * The printed PACE form: four pages, one learner, all three terms.
 *
 * It has its own PDFExport rather than sharing the report card's. Nesting a
 * four-page form inside that wrapper breaks pagination for both, and the two
 * documents have different page setups.
 */
class PacePrintPV extends Component {
  exportPDF = () => {
    this.pdfExportComponent.save()
  }

  ratingOf(competency, macroSkill, term) {
    return this.props.ratings[`${competency.competencyId}|${macroSkill || ''}|${term}`] || ''
  }

  /** Shape A: competencies shared across terms, with macro-skill sub-cells. */
  renderSharedTable(competencies) {
    const { scheme } = this.props
    const periods = scheme.periods

    let currentDomain = null
    const rows = []

    competencies.forEach((competency) => {
      if (competency.domain && competency.domain !== currentDomain) {
        currentDomain = competency.domain
        rows.push(
          <Table.Row key={`d-${competency.competencyId}`} className="pace-domain-row">
            <Table.Cell colSpan={String(periods.length + 2)}>
              <strong>{competency.domain}</strong>
            </Table.Cell>
          </Table.Row>
        )
      }

      rows.push(
        <Table.Row key={competency.competencyId}>
          <Table.Cell collapsing textAlign="center" className="pace-label">
            {competency.label}{competency.subLabel}
          </Table.Cell>
          <Table.Cell className={competency.subLabel ? 'pace-sub' : undefined}>
            {competency.text}
          </Table.Cell>
          {periods.map((period) => {
            const skills = skillsForTerm(competency, period.index)
            return (
              <Table.Cell key={period.index} textAlign="center" className="pace-print-cell">
                {skills.map((skill) => (
                  <span
                    key={skill}
                    className={`pace-print-slot ${SKILL_CLASSES[skill] || ''}`}
                    title={MACRO_SKILL_LABELS[skill] || skill}
                  >
                    {this.ratingOf(competency, skill, period.index)}
                  </span>
                ))}
              </Table.Cell>
            )
          })}
        </Table.Row>
      )
    })

    return (
      <Table celled compact size="small" className="pace-table pace-print-table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell rowSpan="2" textAlign="center">No.</Table.HeaderCell>
            <Table.HeaderCell rowSpan="2" textAlign="center">Learning Competencies</Table.HeaderCell>
            <Table.HeaderCell colSpan={String(periods.length)} textAlign="center">Rating</Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            {periods.map((period) => (
              <Table.HeaderCell key={period.index} collapsing textAlign="center">
                {period.shortLabel}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>{rows}</Table.Body>
      </Table>
    )
  }

  /** Shape B: competencies belong to one term and take a single rating. */
  renderTermScopedTable(competencies) {
    const { scheme } = this.props
    const hasStandard = competencies.some((c) => c.performanceStandard)
    const columnCount = hasStandard ? 4 : 3

    const rows = []

    scheme.periods.forEach((period) => {
      const forTerm = competencies.filter((c) => c.term === period.index)
      if (forTerm.length === 0) return

      rows.push(
        <Table.Row key={`t-${period.index}`} className="pace-term-row">
          <Table.Cell colSpan={String(columnCount)}>
            <strong>{period.label}</strong>
          </Table.Cell>
        </Table.Row>
      )

      let currentDomain = null
      forTerm.forEach((competency) => {
        if (competency.domain && competency.domain !== currentDomain) {
          currentDomain = competency.domain
          rows.push(
            <Table.Row key={`d-${competency.competencyId}`} className="pace-domain-row">
              <Table.Cell colSpan={String(columnCount)}>
                <strong>{competency.domain}</strong>
              </Table.Cell>
            </Table.Row>
          )
        }

        rows.push(
          <Table.Row key={competency.competencyId}>
            <Table.Cell collapsing textAlign="center" className="pace-label">
              {competency.label}{competency.subLabel}
            </Table.Cell>
            <Table.Cell className={competency.subLabel ? 'pace-sub' : undefined}>
              {competency.valueName || competency.text}
            </Table.Cell>
            {hasStandard && (
              <Table.Cell>{competency.performanceStandard}</Table.Cell>
            )}
            <Table.Cell collapsing textAlign="center" className="pace-print-cell">
              {competency.isHeader ? null : (
                <span className="pace-print-slot">
                  {this.ratingOf(competency, '', period.index)}
                </span>
              )}
            </Table.Cell>
          </Table.Row>
        )
      })
    })

    return (
      <Table celled compact size="small" className="pace-table pace-print-table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing textAlign="center">No.</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              {hasStandard ? 'Niililinang na Pagpapahalaga' : 'Learning Competencies'}
            </Table.HeaderCell>
            {hasStandard && <Table.HeaderCell textAlign="center">Performance Standard</Table.HeaderCell>}
            <Table.HeaderCell collapsing textAlign="center">Rating</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{rows}</Table.Body>
      </Table>
    )
  }

  renderIdentity() {
    const { sectionStudent } = this.props
    const student = sectionStudent.student
    const section = sectionStudent.section

    return (
      <div className="pace-print-identity">
        <span>LRN: <u>{student.lrn || ' '.repeat(20)}</u></span>
        <span>
          Name: <u>
            {formatName(student.lastName)}, {formatName(student.firstName)}{' '}
            {formatName(student.middleName)}
          </u>
        </span>
        <span>Section: <u>{section.gradeLevel} - {section.name}</u></span>
      </div>
    )
  }

  render() {
    const { auth, loading, error, sectionStudent, competencies, scheme } = this.props

    if (!auth) return <Redirect to="/" />
    if (loading) return <Loader active inline="centered" />
    if (error) return <Segment textAlign="center">{error}</Segment>
    if (!scheme || !sectionStudent) return <Loader active inline="centered" />

    if (competencies.length === 0) {
      return (
        <Segment textAlign="center">
          No competencies have been loaded for {sectionStudent.section.gradeLevel} yet.
          Run the PACE seed script on the server, then reload this page.
        </Segment>
      )
    }

    const bySubject = {}
    competencies.forEach((c) => {
      (bySubject[c.subjectKey] = bySubject[c.subjectKey] || []).push(c)
    })

    const student = sectionStudent.student

    return (
      <div>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16} textAlign="right">
              <Button primary icon labelPosition="left" onClick={this.exportPDF}>
                <Icon name="print" />
                Print PACE form
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <PDFExport
          ref={(component) => (this.pdfExportComponent = component)}
          paperSize="A4"
          margin="1cm"
          scale={0.55}
          fileName={`PACE Form for ${formatName(student.lastName)}, ${formatName(student.firstName)}`}
          author="SALIS"
        >
          {PAGES.map((page, pageIndex) => {
            const subjectsOnPage = page.subjects.filter((key) => bySubject[key]);
            if (subjectsOnPage.length === 0) return null;

            return (
              <div
                key={page.title}
                className="pace-print-page"
                // Kendo breaks pages on this class.
                style={pageIndex > 0 ? { pageBreakBefore: 'always' } : undefined}
              >
                {this.renderIdentity()}

                {pageIndex === 0 && (
                  <div className="pace-print-instructions">
                    <strong>General Instructions:</strong> {GENERAL_INSTRUCTIONS}
                  </div>
                )}

                {subjectsOnPage.map((key) => {
                  const subjectCompetencies = bySubject[key]
                  const isShared = subjectCompetencies.some((c) => c.shape === 'A')

                  return (
                    <div key={key} className="pace-print-subject">
                      <h3 className="pace-print-heading">
                        {SUBJECT_HEADINGS[key] || page.title}
                      </h3>
                      {isShared
                        ? this.renderSharedTable(subjectCompetencies)
                        : this.renderTermScopedTable(subjectCompetencies)}
                    </div>
                  )
                })}

                {page.subjects.indexOf('Reading and Literacy') !== -1 ||
                page.subjects.indexOf('Language') !== -1 ? (
                  <div className="pace-skill-legend pace-print-legend">
                    <strong>Legend:</strong>
                    {subjectsOnPage
                      .reduce((acc, k) => acc.concat(bySubject[k] || []), [])
                      .length > 0 && skillsUsedBy(
                        subjectsOnPage.reduce((acc, k) => acc.concat(bySubject[k] || []), [])
                      ).map((key) => (
                        <span key={key} className="pace-legend-item">
                          <span className={`pace-swatch ${SKILL_CLASSES[key]}`} />
                          {MACRO_SKILL_LABELS[key]}
                        </span>
                      ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </PDFExport>
      </div>
    )
  }
}

export default PacePrintPV
